// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import { connect, useSelector } from "react-redux";
import React, { useCallback, useEffect } from "react";
import { HStack, Switch, FormControl, FormLabel } from "@chakra-ui/react";
import * as R from "ramda";

import ProcessRefresh from "./Refresh";
import ReportsRefresh from "src/components/Manage/Reports/Instances/Refresh";
import Share from "./Share";
import Customize from "./Customize";
import { MenuBar as StyledMenuBar, InstanceCount } from "./styles";
import {
  getInstanceFilterId,
  getInstanceReportId,
  getReportsTableColumns,
  getProcessTableColumns,
  getPrincipalChecklist,
  getAllPrincipalChecklistFields,
  getLoadingInstances,
  getComponentPermission
} from "src/reducers";
import Name from "src/containers/workflow/Name";
import { setNewWorkflow } from "src/actions/chatroom";
import useBoolean from "src/hooks/useBoolean";
import AddChatroom from "src/containers/chatroom/creator";
import Icon from "src/icons";
import UploadCSV from "./UploadCSV";
import { reOrderProcessColumn } from "src/actions/workflows";
import { mergeNewFields } from "src/utils/checklist";
import BlueOutlineButton from "src/components/Buttons/BlueOutline";
import { componentPermissions } from "src/constants/roleManagement";
import ExportMenu from "./ExportMenu";

import type { AppState } from "src/types";

type Props = {
  id: number,
  _setNewWorkflow: Function,
  _reOrderProcessColumn: Function,
  fields: Array<Object>,
  options: Array<Object>,
  principalChecklistTitle: ?string,
  rowCount: number,
  loading: boolean,
  reportId: ?number,
  getHeaderGroups: Function,
  getRowModel: Function,
  setRevisionsShown: Function,
  allRevisionsShown: Boolean
};

const MenuBar = ({
  id,
  _setNewWorkflow,
  _reOrderProcessColumn,
  options,
  fields,
  principalChecklistTitle,
  rowCount,
  loading,
  reportId,
  getHeaderGroups,
  getRowModel,
  setRevisionsShown,
  allRevisionsShown
}: Props) => {
  const { value: newConversation, toggleBoolean: toggleConversation } =
    useBoolean();
  const RBAC = useSelector(({ app }) =>
    getComponentPermission(app, componentPermissions.csvUpload)
  );
  const showUploadCSVButton = RBAC;

  const handleNewConversation = useCallback(() => {
    _setNewWorkflow(id);
    toggleConversation();
  }, [id, toggleConversation, _setNewWorkflow]);

  useEffect(() => {
    // Don't run if fields data is not fetched yet
    if (!R.isNil(principalChecklistTitle)) {
      if (!reportId) {
        const optionsIncludesFields = options.filter(f => f.label).length > 0;

        const optionsWithFields = [
          ...(optionsIncludesFields
            ? mergeNewFields(fields, options)
            : options),
          ...(fields && !optionsIncludesFields
            ? fields.map(field => ({ ...field, key: field.id, active: true }))
            : [])
        ];

        _reOrderProcessColumn(id, optionsWithFields);
      }
    }
  }, [fields, principalChecklistTitle]);

  return (
    <StyledMenuBar>
      <InstanceCount>
        {loading
          ? i18n.t(k.LOADING_RESULTS)
          : `${rowCount} ${i18n.t(k.RESULTS)}`}
      </InstanceCount>
      <HStack spacing={4} position="relative" zIndex={3}>
        <FormControl display="flex" alignItems="center" w="fit-content" pl={4}>
          <FormLabel htmlFor="showRevisions" mb="0">
            {i18n.t(k.SHOW_ALL_REVISIONS)}
          </FormLabel>
          <Switch
            id="showRevisions"
            onChange={() => setRevisionsShown(!allRevisionsShown)}
          />
        </FormControl>

        {showUploadCSVButton && <UploadCSV templateId={id} />}

        <BlueOutlineButton
          leftIcon={<Icon type="circlePlus" />}
          onClick={handleNewConversation}
        >
          {i18n.t(k.NEW1)} <Name id={id} />
        </BlueOutlineButton>

        <Customize />

        {reportId ? <ReportsRefresh /> : <ProcessRefresh />}

        <ExportMenu
          getHeaderGroups={getHeaderGroups}
          getRowModel={getRowModel}
        />

        <Share />
      </HStack>

      {newConversation && (
        <AddChatroom
          show={newConversation}
          position="center"
          modalId="chatroomSearch"
          handleClose={toggleConversation}
          hidePinTo
        />
      )}
    </StyledMenuBar>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => {
  const id = getInstanceFilterId(app);
  const reportId = getInstanceReportId(app);
  const principalChecklist = getPrincipalChecklist(app);
  return {
    id,
    options: reportId
      ? getReportsTableColumns(app, reportId)
      : getProcessTableColumns(app, id || ""),
    fields: getAllPrincipalChecklistFields(app),
    principalChecklistTitle: principalChecklist.title,
    loading: getLoadingInstances(app)
  };
};

export default connect(mapStateToProps, {
  _setNewWorkflow: setNewWorkflow,
  _reOrderProcessColumn: reOrderProcessColumn
})(MenuBar);
